import { getTableColumns } from "drizzle-orm";
import { index, pgTable, uuid } from "drizzle-orm/pg-core";
import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import { z } from "zod";

import {
  applicationTypeEnum,
  applicationTypeEnumSchema,
} from "./applicationsSchema";
import {
  eligibilityCategoriesTable,
  federalGroupEnumSchema,
} from "./eligibilityCategoriesSchema";
import {
  householdMemberSchema,
  householdMembersTable,
} from "./householdMembersSchema";
import { householdsTable } from "./householdsSchema";
import { customTimestamp } from "./utils/types";

export const eligibilityResultsTable = pgTable(
  "eligibility_results",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    householdMemberId: uuid("household_member_id")
      .references(() => householdMembersTable.id, {
        onDelete: "cascade",
      })
      .notNull(),
    householdId: uuid("household_id")
      .references(() => householdsTable.id, {
        onDelete: "cascade",
      })
      .notNull(),
    applicationType: applicationTypeEnum("application_type").notNull(),
    eligibilityCategoryId: uuid("eligibility_category_id")
      .references(() => eligibilityCategoriesTable.id, {
        onDelete: "cascade",
      })
      .notNull(),
    createdAt: customTimestamp("created_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString()),
    updatedAt: customTimestamp("updated_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString())
      .$onUpdateFn(() => new Date().toISOString()),
  },
  table => [
    index("eligibility_results_household_member_idx").on(
      table.householdMemberId,
    ),
    index("eligibility_results_household_member_category_idx").on(
      table.householdMemberId,
      table.eligibilityCategoryId,
    ),
    index("eligibility_results_household_member_application_type_idx").on(
      table.householdMemberId,
      table.applicationType,
    ),
  ],
);

export const eligibilityResultsSchema = createSelectSchema(
  eligibilityResultsTable,
  {
    applicationType: applicationTypeEnumSchema,
  },
);
export type EligibilityResultsType = z.infer<typeof eligibilityResultsSchema>;

export const insertEligibilityResultSchema = createInsertSchema(
  eligibilityResultsTable,
  {
    householdMemberId: z.uuid(),
    applicationType: applicationTypeEnumSchema,
    eligibilityCategoryId: z.uuid(),
  },
).omit({
  createdAt: true,
  updatedAt: true,
});

export type InsertEligibilityResultType = z.infer<
  typeof insertEligibilityResultSchema
>;

export type SyncEligibilityResultItemType = Omit<
  InsertEligibilityResultType,
  "id" | "householdId"
>;

export const eligibilityResultsTableColumns = getTableColumns(
  eligibilityResultsTable,
);

export const eligibilityResultWithMemberNameSchema =
  eligibilityResultsSchema.extend({
    householdMember: householdMemberSchema,
    customUsStateName: z.string().nullable(),
    federalGroup: federalGroupEnumSchema,
  });
